import type { ReactNode } from 'react';
import type { ColumnDef } from '@/hooks/useColumnPreferences';
import type { LeadRow } from './rows';

/** Which table a column belongs to: a folder's leads, the Creators screen, or both. */
export type TableScope = 'folder' | 'creators';

export interface LeadColumn {
  id: string;
  header: string;
  cell: (row: LeadRow) => ReactNode;
  /** Server sort param for this column (`followers` → `?sort=-followers` / `followers`). */
  sortKey?: string;
  defaultVisible: boolean;
  /** Can't be hidden from the Columns picker. */
  locked?: boolean;
  numeric?: boolean;
  scopes: readonly TableScope[];
}

export function columnsForScope(columns: readonly LeadColumn[], scope: TableScope): LeadColumn[] {
  return columns.filter((c) => c.scopes.includes(scope));
}

/** Shape the shared ColumnPicker + useColumnPreferences expect. */
export function toColumnDefs(columns: readonly LeadColumn[]): ColumnDef[] {
  return columns.map((c) => ({ key: c.id, label: c.header, locked: c.locked, defaultVisible: c.defaultVisible }));
}

export function defaultVisibleIds(columns: readonly LeadColumn[]): string[] {
  return columns.filter((c) => c.locked || c.defaultVisible).map((c) => c.id);
}

export function visibleColumns(columns: readonly LeadColumn[], visible: ReadonlySet<string>): LeadColumn[] {
  return columns.filter((c) => c.locked || visible.has(c.id));
}

export type SortDirection = 'asc' | 'desc' | null;

/** Direction `sort` applies to `key` (`-key` = descending), or null if it sorts something else. */
export function sortDirection(sort: string, key: string): SortDirection {
  if (sort === key) return 'asc';
  if (sort === `-${key}`) return 'desc';
  return null;
}

/**
 * Header click cycle: numbers read best biggest-first, so a fresh column
 * starts descending, then flips to ascending, then back.
 */
export function nextSort(sort: string, key: string): string {
  const dir = sortDirection(sort, key);
  if (dir === 'desc') return key;
  return `-${key}`;
}
